<script setup lang="ts">
import { inject, ref } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import JobEditSectionHeader from '@/components/headers/JobEditSectionHeader.vue';
import { IsDraggingKey } from '@/composables/jobEdit/useJobEditSectionsOrder';

export interface Props {
  title?: string;
  initiallyCollapsed?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
  editable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  initiallyCollapsed: false,
  isEditing: false,
  isLoading: false,
  editable: false,
});

const emit = defineEmits<{
  cancel: [];
  save: [];
  editBtnClick: [event: Event];
}>();

const isCollapsed = ref(props.initiallyCollapsed);
const isDragging = inject(IsDraggingKey, ref(false));

function handleHeaderClick(): void {
  isCollapsed.value = !isCollapsed.value;
}
</script>

<template>
  <div
    :class="$style.jobEditAccordionSection"
    data-testid="jobEditAccordionSection"
  >
    <JobEditSectionHeader
      :title="props.title"
      :collapsed="isCollapsed"
      :is-editing="props.isEditing"
      :is-loading="props.isLoading"
      @click="handleHeaderClick"
      @cancel="emit('cancel')"
      @save="emit('save')"
    >
      <template #collapsedContent>
        <slot name="collapsedContent" />
      </template>
      <template #actions>
        <KBtn
          v-if="props.editable && !props.isEditing"
          variant="text"
          size="sm"
          :loading="props.isLoading"
          @click.stop="emit('editBtnClick', $event)"
        >
          EDIT
        </KBtn>
        <slot name="headerActions" />
      </template>
    </JobEditSectionHeader>

    <transition name="expand">
      <div v-show="!isCollapsed && !isDragging" :class="$style.content">
        <slot />
      </div>
    </transition>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditAccordionSection {
  border-radius: 8px;
  border: 1px solid colors.$color-gray-200;
  margin-bottom: 16px;
  background-color: colors.$color-white;
}

.content {
  padding: 16px;
}
</style>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
